import { GrafoDto, NodoDto } from "@/api";
import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";
import { useEdgesState, useNodesState } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { groupBy, keyBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

const nodeWidth = 100;
const nodeHeight = 75;

const getLayoutedElements = (nodes: any, edges: any, direction = "TB") => {
  const isHorizontal = direction === "LR";

  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 10, // Distanza orizzontale tra nodi sullo stesso livello
    ranksep: 65, // Distanza verticale tra livelli
    marginx: 50, // Margine orizzontale
    marginy: 50, // Margine verticale
  });

  nodes.forEach((node: any) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge: any) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node: any) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const newNode = {
      ...node,
      targetPosition: isHorizontal ? "left" : "top",
      sourcePosition: isHorizontal ? "right" : "bottom",
      position: {
        x: nodeWithPosition.x,
        y: nodeWithPosition.y,
      },
      style: {
        width: nodeWidth,
        height: nodeHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontSize: "10px",
        wordWrap: "break-word",
        padding: 0,
      },
    };

    return newNode;
  });

  return { nodes: layoutedNodes, edges };
};

export function useLayoutedReactFlowNodesAndEdges(
  azienda: NodoDto | null,
  grafoData: GrafoDto | null,
  additionalNodeDataProps?: any,
  skipGatewayERipetitori = false
) {
  const nodoById = useMemo(() => {
    const nodi = grafoData?.nodi ?? [];

    if (azienda) {
      nodi.push(azienda);
    }

    if (grafoData?.nodi) {
      return keyBy(nodi, (x) => x.id);
    } else {
      return {};
    }
  }, [grafoData, azienda]);

  const archiByDestinazioneId = useMemo(() => {
    if (grafoData?.archi) {
      return groupBy(grafoData?.archi, (x) => x.destinazioneId);
    } else {
      return {};
    }
  }, [grafoData]);

  const archiBySorgenteId = useMemo(() => {
    if (grafoData?.archi) {
      return groupBy(grafoData?.archi, (x) => x.sorgenteId);
    } else {
      return {};
    }
  }, [grafoData]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  nodesRef.current = nodes;
  edgesRef.current = edges;

  const firstRenderRef = useRef(true);
  useEffect(() => {
    firstRenderRef.current = true;
  }, [azienda?.id]);

  useEffect(() => {
    if (azienda && grafoData?.nodi && grafoData.archi) {
      const nodiFiltrati =
        grafoData.nodi.filter(
          (nodo: NodoDto) =>
            nodo.tipo == "Azienda" ||
            nodo.tipo == "Contenitore" ||
            nodo.tipo == "Gateway" ||
            nodo.tipo == "Ripetitore"
        ) || [];
      const nodi = [azienda, ...nodiFiltrati]; // Azienda viene messa in testa

      const newReactFlowNodes: Node[] = (nodi || [])
        .map((nodo, index) => {
          if (
            skipGatewayERipetitori &&
            (nodo.tipo === "Gateway" || nodo.tipo === "Ripetitore")
          ) {
            return null;
          }

          const reactFlowNodo = nodes.find((x) => parseInt(x.id) === nodo.id);

          if (reactFlowNodo) {
            return {
              ...reactFlowNodo,
              data: {
                nodo: nodo,
                archiByDestinazioneId,
                archiBySorgenteId,
                ...additionalNodeDataProps,
              },
            };
          } else {
            let x = 0;
            let y = 0;

            // se il padre ha le coordinate, utilizzo le sue per disporre il figlio sotto di lui
            // questa logica è necessaria per quando viene aggiunto un nuovo nodo figlio
            const padri = archiByDestinazioneId[nodo.id] ?? [];
            for (const padre of padri) {
              const nodoPadre = nodoById[padre.sorgenteId!];
              if (
                nodoPadre.tipo === "Azienda" ||
                nodoPadre.tipo === "Contenitore"
              ) {
                const reactFlowPadre = nodesRef.current.find(
                  (x) => parseInt(x.id) === nodoPadre.id
                );
                if (
                  reactFlowPadre &&
                  reactFlowPadre.position.x !== 0 &&
                  reactFlowPadre.position.x !== 1
                ) {
                  x = reactFlowPadre.position.x;
                  y = reactFlowPadre.position.y + 50;
                  break;
                }
              }
            }

            return {
              id: nodo.id?.toString() || index.toString(),
              type:
                nodo.tipo === "Gateway"
                  ? "gateway"
                  : nodo.tipo === "Ripetitore"
                  ? "ripetitore"
                  : "default",
              position: {
                x: x,
                y: y,
              },
              data: {
                nodo: nodo,
                archiByDestinazioneId,
                archiBySorgenteId,
                ...additionalNodeDataProps,
              },
            };
          }
        })
        .filter((x) => !!x) as Node[];

      const newReactFlowEdges: Edge[] = grafoData.archi
        .filter(
          (x) =>
            x.destinazione?.tipo === "Contenitore" ||
            x.destinazione?.tipo === "Gateway" ||
            x.destinazione?.tipo === "Ripetitore"
        )
        .map((arco, index) => {
          const reactFlowEdge = edgesRef.current.find(
            (x) =>
              parseInt(x.source) === arco.sorgenteId! &&
              parseInt(x.target) === arco.destinazioneId!
          );

          if (reactFlowEdge) {
            return reactFlowEdge;
          } else {
            return {
              id: `edge_${arco.id}`,
              source: arco.sorgenteId!.toString(),
              target: arco.destinazioneId!.toString(),
              type: "default",
            };
          }
        });

      if (firstRenderRef.current) {
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(newReactFlowNodes, newReactFlowEdges, "TB");

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);

        firstRenderRef.current = false;
      } else {
        setNodes(newReactFlowNodes);
        setEdges(newReactFlowEdges);
      }
    }
  }, [
    azienda,
    grafoData,
    archiByDestinazioneId,
    archiBySorgenteId,
    additionalNodeDataProps,
    skipGatewayERipetitori,
  ]);

  const autoLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      "TB"
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [nodes, edges]);

  return {
    nodes: nodes,
    edges: edges,
    nodoById: nodoById,
    archiByDestinazioneId: archiByDestinazioneId,
    autoLayout: autoLayout,
    onNodesChange: onNodesChange,
    onEdgesChange: onEdgesChange,
  };
}
